import React, { useState } from 'react';
import { FlowerButton } from '@/components/ui/FlowerButton';

export const FlowerButtonTest: React.FC = () => {
  const [centerX, setCenterX] = useState(240.5);
  const [centerY, setCenterY] = useState(396.5);

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Center Point Adjustment
        </h1>
        <p className="text-gray-600 mb-8">
          Adjust the sliders to position the center dot exactly between the four petals
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Flower display */}
          <div className="bg-white rounded-lg shadow-md p-8 flex flex-col items-center justify-center">
            <FlowerButton
              size={200}
              defaultOpen={true}
              showCenterDot={true}
              centerX={centerX}
              centerY={centerY}
            />
            <p className="mt-6 text-sm text-gray-600 font-mono">
              Center: ({centerX.toFixed(2)}, {centerY.toFixed(2)})
            </p>
          </div>

          {/* Controls */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Position Controls</h3>

            {/* X Position */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                X Position: {centerX.toFixed(2)}
              </label>
              <input
                type="range"
                min="100"
                max="550"
                step="0.1"
                value={centerX}
                onChange={(e) => setCenterX(parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>100</span>
                <span>550</span>
              </div>
            </div>

            {/* Y Position */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Y Position: {centerY.toFixed(2)}
              </label>
              <input
                type="range"
                min="100"
                max="550"
                step="0.1"
                value={centerY}
                onChange={(e) => setCenterY(parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>100</span>
                <span>550</span>
              </div>
            </div>

            {/* Reset button */}
            <button
              onClick={() => {
                setCenterX(240.5);
                setCenterY(396.5);
              }}
              className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-900 rounded-md transition-colors"
            >
              Reset to Optimal Center
            </button>

            {/* Current values for copying */}
            <div className="mt-6 p-4 bg-gray-100 rounded">
              <p className="text-xs font-semibold text-gray-700 mb-2">Copy these values:</p>
              <code className="text-sm text-gray-900">
                centerX={centerX.toFixed(2)}
                <br />
                centerY={centerY.toFixed(2)}
              </code>
            </div>

            {/* Presets */}
            <div className="mt-6">
              <p className="text-sm font-semibold text-gray-700 mb-2">Quick Presets:</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => {
                    setCenterX(240.5);
                    setCenterY(396.5);
                  }}
                  className="px-3 py-2 bg-blue-100 hover:bg-blue-200 text-blue-900 rounded text-sm transition-colors"
                >
                  Optimal (240.5, 396.5)
                </button>
                <button
                  onClick={() => {
                    setCenterX(318.74);
                    setCenterY(318.825);
                  }}
                  className="px-3 py-2 bg-green-100 hover:bg-green-200 text-green-900 rounded text-sm transition-colors"
                >
                  ViewBox Center
                </button>
                <button
                  onClick={() => {
                    setCenterX(250);
                    setCenterY(400);
                  }}
                  className="px-3 py-2 bg-purple-100 hover:bg-purple-200 text-purple-900 rounded text-sm transition-colors"
                >
                  250, 400
                </button>
                <button
                  onClick={() => {
                    setCenterX(230);
                    setCenterY(390);
                  }}
                  className="px-3 py-2 bg-pink-100 hover:bg-pink-200 text-pink-900 rounded text-sm transition-colors"
                >
                  230, 390
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">Instructions:</h3>
          <ol className="list-decimal list-inside space-y-1 text-blue-800">
            <li>The flower above is shown in the OPEN state</li>
            <li>Use the sliders to move the red center dot</li>
            <li>Position it exactly between the four petals</li>
            <li>Copy the final coordinates and let me know!</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default FlowerButtonTest;
